import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import { useAppTour } from "@/hooks/useAppTour";
import { User, Mail, Calendar, CreditCard, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UserData, TierData } from "@/types/user";

type SettingsSection = "profile" | "billing";

const settingsSections: Array<{
  id: SettingsSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: "profile", label: "Profile", icon: User },
  { id: "billing", label: "Billing", icon: CreditCard },
];

/**
 * Render the profile details card for a user.
 *
 * Displays the user's avatar (or a placeholder), name, username, email, and an optional "Member Since" date.
 *
 * @param userData - The user record used to populate avatar, display name, username, email, and created_at (membership date).
 * @returns A React element containing a card with the user's profile information and related UI elements.
 */
function ProfileSection({ userData }: { userData: UserData }) {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              {userData.profile_image_url &&
              userData.profile_image_url !== "https://profileimageurl.com" ? (
                <img
                  src={userData.profile_image_url}
                  alt={userData.name}
                  className="h-24 w-24 rounded-full object-cover border-2 border-border shadow-sm"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border-2 border-border shadow-sm">
                  <User className="h-12 w-12 text-primary/60" />
                </div>
              )}
              <div>
                <h3 className="text-2xl font-semibold tracking-tight">{userData.name}</h3>
                <p className="text-muted-foreground mt-1">@{userData.username}</p>
              </div>
            </div>

            <Separator />

            {/* User Details Grid */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  Email
                </div>
                <p className="text-base font-medium">{userData.email}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <User className="h-4 w-4" />
                  Username
                </div>
                <p className="text-base font-medium">@{userData.username}</p>
              </div>

              {userData.created_at && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Member Since
                  </div>
                  <p className="text-base font-medium">{formatDate(userData.created_at)}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Renders the Billing settings section showing the user's current subscription and an optional upgrade prompt.
 *
 * Displays a "Current Plan" card that adapts to loading state, available tier details, or absence of a subscription.
 *
 * @param userData - The current user's data (used to read `tier_id` and display fallback information).
 * @param tierData - The resolved tier details for the user's subscription, or `null` if not available.
 * @param isLoading - Whether tier details are currently being loaded; when true, the component shows loading placeholders.
 * @returns The billing section UI as a JSX element.
 */
function BillingSection({
  userData,
  tierData,
  isLoading: tierLoading,
}: {
  userData: UserData;
  tierData: TierData | null;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Current Plan
          </CardTitle>
          <CardDescription>Your active subscription tier</CardDescription>
        </CardHeader>
        <CardContent>
          {tierLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          ) : tierData ? (
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">{tierData.name.charAt(0).toUpperCase() + tierData.name.slice(1)}</h3>
              </div>
              <Badge variant="secondary" className="text-sm">
                Active
              </Badge>
            </div>
          ) : userData.tier_id ? (
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">Tier ID: {userData.tier_id}</p>
              <Badge variant="outline">Loading details...</Badge>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">No active subscription</p>
              <Badge variant="outline">Free</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Prompt */}
      {!tierData && !tierLoading && (
        <Card className="border-primary/50 bg-primary/5 shadow-sm">
          <CardHeader>
            <CardTitle>Upgrade Your Plan</CardTitle>
            <CardDescription>Unlock additional features and capabilities</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Upgrade to access premium features and higher limits.
            </p>
            <p className="text-xs text-muted-foreground">
              Contact your administrator or support team to upgrade your subscription.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Render the Settings page with a left navigation for sections and a main area showing profile or billing content.
 *
 * Synchronizes the active section with the URL search param `section`, fetches the current user and (when applicable) tier details, and displays loading, error, profile, or billing states accordingly.
 *
 * @returns The page's JSX element containing the header, sidebar navigation, and main content for settings.
 */
export function SettingsPage() {
  const api = useApi();
  const { restartTour } = useAppTour();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get section from URL or default to profile
  const sectionParam = searchParams.get("section") as SettingsSection | null;
  const activeSection: SettingsSection =
    sectionParam && settingsSections.some((s) => s.id === sectionParam) ? sectionParam : "profile";

  const setActiveSection = (section: SettingsSection) => {
    setSearchParams({ section });
  };

  const {
    data: userData,
    isLoading,
    error,
  } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const data = await api.get<UserData>("/api/v1/user/me/");
      return data;
    },
    retry: 1,
  });

  const { data: tierData, isLoading: tierLoading } = useQuery<TierData | null>({
    queryKey: ["tier", userData?.tier_id],
    queryFn: async () => {
      if (!userData?.tier_id) return null;
      const data = await api.get<TierData>(`/api/v1/tiers/${userData.tier_id}`);
      return data;
    },
    enabled: !!userData?.tier_id,
    retry: 1,
  });

  return (
    <div className="container mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground mt-2">
              Manage your account settings and set e-mail preferences.
            </p>
          </div>
          <Button variant="outline" onClick={restartTour} className="flex items-center gap-2">
            <Play className="h-4 w-4" />
            Take a Tour
          </Button>
        </div>
      </div>

      {/* Sidebar and Content */}
      <div className="flex flex-1 gap-6">
        {/* Sidebar Navigation */}
        <aside className="w-64 shrink-0">
          <div className="sticky top-6">
            <nav className="space-y-1">
              {settingsSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {isLoading ? (
            <div className="space-y-6">
              <Skeleton className="h-8 w-48" />
              <Card className="shadow-sm">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-24 w-24 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-32" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                  <Separator />
                  <div className="grid gap-6 md:grid-cols-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : error ? (
          <Card className="border-destructive shadow-sm">
            <CardHeader>
              <CardTitle className="text-destructive">Error Loading Settings</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : "Failed to load user data"}
              </CardDescription>
            </CardHeader>
          </Card>
        ) : userData ? (
          <>
            {activeSection === "profile" && <ProfileSection userData={userData} />}
            {activeSection === "billing" && (
              <BillingSection
                userData={userData}
                tierData={tierData ?? null}
                isLoading={tierLoading}
              />
            )}
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}
